import React, { useState, useMemo } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { ArrowLeft, Clock, ArrowRight } from 'lucide-react';
import { Button } from './ui/button';
import { blogPosts } from '../data/blogPosts';
import { Seo } from './Seo';
import BookingCalendar from './BookingCalendar';

const BlogPostPage = () => {
  const { slug } = useParams();
  const [bookingOpen, setBookingOpen] = useState(false);
  const post = blogPosts.find((p) => p.slug === slug);

  const jsonLdSafe = useMemo(() => {
    if (!post) return '';
    return JSON.stringify({
      '@context': 'https://schema.org',
      '@type': 'Article',
      headline: post.title,
      description: post.metaDescription,
      image: post.image,
      datePublished: post.date,
      author: { '@type': 'Organization', name: 'Dublin Holistic Massage' },
      publisher: { '@type': 'Organization', name: 'Dublin Holistic Massage', url: 'https://www.dublinholisticmassages.com' },
    }).replace(/</g, '\\u003c');
  }, [post]);

  if (!post) return <Navigate to="/blog" replace />;

  const related = blogPosts.filter((p) => p.slug !== slug).slice(0, 2);

  return (
    <div className="min-h-screen bg-alabaster-100" data-testid="blog-post-page">
      <Seo title={`${post.title} | Dublin Holistic Massage`} description={post.metaDescription} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: jsonLdSafe }} />

      <div className="max-w-3xl mx-auto px-6 sm:px-8 py-16 sm:py-20">
        <Link to="/blog" className="inline-flex items-center gap-2 text-sage-600 hover:text-luxury-green text-sm mb-10 transition-colors" data-testid="blog-post-back">
          <ArrowLeft size={16} />
          All articles
        </Link>

        <div className="flex items-center gap-3 text-stone-400 text-xs mb-4">
          <Clock size={12} />
          {post.readTime}
          <span>·</span>
          {new Date(post.date).toLocaleDateString('en-IE', { day: 'numeric', month: 'long', year: 'numeric' })}
        </div>
        <h1 className="font-display text-3xl sm:text-4xl lg:text-5xl text-luxury-green-900 leading-tight mb-8">{post.title}</h1>

        <div className="rounded-3xl overflow-hidden mb-10">
          <img src={post.image} alt={post.title} className="w-full h-72 sm:h-96 object-cover" />
        </div>

        {post.sections.map((s) => (
          <div key={s.heading} className="mb-8">
            <h2 className="font-display text-2xl text-luxury-green-900 mb-4">{s.heading}</h2>
            {s.paragraphs.map((p) => (
              <p key={p.slice(0, 60)} className="text-stone-600 font-light leading-relaxed mb-4">{p}</p>
            ))}
          </div>
        ))}

        <div className="bg-luxury-green-800 rounded-3xl p-8 sm:p-10 text-center my-12">
          <h3 className="font-display text-2xl text-alabaster-100 mb-2">Ready to feel the difference?</h3>
          <p className="text-alabaster-300 text-sm font-light mb-6">Book online in under two minutes — first visit gets 5% off automatically.</p>
          <Button
            onClick={() => setBookingOpen(true)}
            className="bg-luxury-gold hover:bg-luxury-gold-600 text-luxury-green-900 rounded-full py-6 px-10 text-sm uppercase tracking-wider font-bold"
            data-testid="blog-post-book-btn"
          >
            Book Your Massage
            <ArrowRight size={15} className="ml-2" />
          </Button>
        </div>

        <h3 className="font-display text-xl text-luxury-green-900 mb-5">Keep reading</h3>
        <div className="grid sm:grid-cols-2 gap-5">
          {related.map((r) => (
            <Link
              key={r.slug}
              to={`/blog/${r.slug}`}
              className="group bg-white rounded-2xl border border-stone-200 hover:border-luxury-gold/60 p-5 transition-colors"
            >
              <h4 className="font-display text-lg text-luxury-green-900 leading-snug mb-2">{r.title}</h4>
              <span className="text-luxury-gold-700 text-xs uppercase tracking-wider font-semibold inline-flex items-center gap-1">
                Read <ArrowRight size={12} className="group-hover:translate-x-1 transition-transform" />
              </span>
            </Link>
          ))}
        </div>
      </div>

      <BookingCalendar isOpen={bookingOpen} onClose={() => setBookingOpen(false)} />
    </div>
  );
};

export default BlogPostPage;
